import * as React from 'react'
import SpeedDial from '@mui/material/SpeedDial'
import SpeedDialAction from '@mui/material/SpeedDialAction'
import Fab from '@mui/material/Fab'
import LaunchGrid from './LaunchGrid'

import AbcIcon from '@mui/icons-material/Abc'
import TranslateIcon from '@mui/icons-material/Translate'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ChatIcon from '@mui/icons-material/Chat'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import StyleIcon from '@mui/icons-material/Style'
import EventNoteIcon from '@mui/icons-material/EventNote'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SpellcheckIcon from '@mui/icons-material/Spellcheck'
import ScienceIcon from '@mui/icons-material/Science'
import SchoolIcon from '@mui/icons-material/School'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import PersonIcon from '@mui/icons-material/Person'
import ReplayIcon from '@mui/icons-material/Replay'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SmsIcon from '@mui/icons-material/Sms'
import GTranslateIcon from '@mui/icons-material/GTranslate'
import QueueIcon from '@mui/icons-material/Queue'
import EventIcon from '@mui/icons-material/Event'

export default function HallucinationButton({
  toggleViewHallucination,
  toggleViewTranslation,
  toggleViewSpeech,
  toggleViewManyInspirations,
  toggleManyTranslationsFullView,
  toggleViewLanguagePartner,
  toggleViewKanban,
  toggleViewTaskBoard,
  toggleViewSpeechPractice,
  toggleFlashcardView,
  toggleMeetingView,
  toggleParagraphView,
  openAiLesson,
  openImportLesson,
  openProfile,
  openReviewDue,
  openIntroduce,
  openHomework,
  openAddSentence,
  openAddSentenceAll,
  openAddSentencesExcel,
  openAddMeetingDoc,
  toggleVerbLab,
  toggleSpecialMode,
  toggleLanguageTree
}) {
  // Trial: the same Learn menu as a full-screen grid instead of a fan of circles.
  const [gridOpen, setGridOpen] = React.useState(false)

  // The newest learning features — split out of the (previously 19-item) views
  // menu into their own button so neither dial is overloaded.
  const learnActions = [
    // `group` drives the sections in the full-screen grid; the SpeedDial just
    // follows the same order.
    { group: 'Practice', icon: <AccountTreeIcon />, name: 'Skill Tree', onClick: () => toggleLanguageTree && toggleLanguageTree() },
    { group: 'Practice', icon: <ScienceIcon />, name: 'Special Mode', onClick: () => toggleSpecialMode && toggleSpecialMode() },
    { group: 'Practice', icon: <SpellcheckIcon />, name: 'Verb Lab', onClick: () => toggleVerbLab && toggleVerbLab() },
    { group: 'Practice', icon: <ReplayIcon />, name: 'Review due', onClick: () => openReviewDue && openReviewDue() },
    { group: 'Practice', icon: <EmojiPeopleIcon />, name: 'Introduce Yourself', onClick: () => openIntroduce && openIntroduce() },

    { group: 'Lessons', icon: <AssignmentIcon />, name: 'Homework', onClick: () => openHomework && openHomework() },
    { group: 'Lessons', icon: <AutoAwesomeIcon />, name: 'AI Lesson', onClick: () => openAiLesson && openAiLesson() },
    { group: 'Lessons', icon: <SchoolIcon />, name: 'Import Lesson', onClick: () => openImportLesson && openImportLesson() },

    // Adding study material — moved here from the Language dial. Meeting
    // Documents is also still on that dial.
    { group: 'Add material', icon: <SmsIcon />, name: 'Sentence', onClick: () => openAddSentence && openAddSentence() },
    { group: 'Add material', icon: <GTranslateIcon />, name: 'Translate to all', onClick: () => openAddSentenceAll && openAddSentenceAll() },
    { group: 'Add material', icon: <QueueIcon />, name: 'Sentences (Bulk)', onClick: () => openAddSentencesExcel && openAddSentencesExcel() },
    { group: 'Add material', icon: <EventIcon />, name: 'Meeting Documents', onClick: () => openAddMeetingDoc && openAddMeetingDoc() },

    { group: 'You', icon: <PersonIcon />, name: 'Profile', onClick: () => openProfile && openProfile() }
  ]

  // The original content views.
  const viewActions = [
    { icon: <AbcIcon />, name: 'Inspirations View', onClick: () => toggleViewHallucination() },
    { icon: <TranslateIcon />, name: 'Translation View', onClick: () => toggleViewTranslation() },
    { icon: <RecordVoiceOverIcon />, name: 'Speech Translation', onClick: () => toggleViewSpeech() },
    { icon: <RecordVoiceOverIcon />, name: 'Speech Practice', onClick: () => toggleViewSpeechPractice() },
    { icon: <ChatIcon />, name: 'Language Partner', onClick: () => toggleViewLanguagePartner() },
    { icon: <ChatIcon />, name: 'Many Translations', onClick: () => toggleManyTranslationsFullView() },
    { icon: <StyleIcon />, name: 'Flashcards', onClick: () => toggleFlashcardView() },
    { icon: <EventNoteIcon />, name: 'Meetings', onClick: () => toggleMeetingView() },
    { icon: <MenuBookIcon />, name: 'Paragraphs', onClick: () => toggleParagraphView() }
    // Media and Journal moved to the Work dial (AddButtonWork).
    // { icon: <ViewKanbanIcon />, name: 'Kanban', onClick: () => toggleViewKanban() },
    // { icon: <ViewKanbanIcon />, name: 'Task Board', onClick: () => toggleViewTaskBoard() }
    // { icon: <AbcIcon />, name: 'Many Inspirations', onClick: () => toggleViewManyInspirations() }
  ]

  return (
    <>
      <SpeedDial
        direction="up"
        ariaLabel="SpeedDialViews"
        icon={<PlayArrowIcon />}
        FabProps={{ sx: fabSx('#2563eb', '#1d4ed8') }}>
        {viewActions.map((action) => (
          <SpeedDialAction key={action.name} icon={action.icon} tooltipTitle={action.name} onClick={action.onClick} />
        ))}
      </SpeedDial>
      {/* The Learn button: a plain Fab that opens the full-screen grid, in place
          of the old fan-out dial.
          - pointerEvents: the HUD row inherits `pointer-events: none` (Hud.js
            `base`); MUI's SpeedDial re-enables it on its own Fab internally, so
            a bare Fab has to do the same or it ignores clicks.
          - alignSelf: the row is `align-items: stretch` and a `direction="up"`
            SpeedDial reserves height for all its hidden actions, so an
            unstretched Fab would sit at the TOP of that tall row.
          - zIndex matches theme.zIndex.speedDial (1050). */}
      <Fab aria-label="Learn" title="Learn"
        sx={{ ...fabSx('#059669', '#047857'), pointerEvents: 'auto', zIndex: 1050, alignSelf: 'flex-end' }}
        onClick={() => setGridOpen(true)}>
        <SchoolIcon />
      </Fab>
      {gridOpen && (
        <LaunchGrid title="Learn" subtitle="Pick one to open it." actions={learnActions} onClose={() => setGridOpen(false)} />
      )}
    </>
  )
}

// Shared Fab colouring for the floating menu buttons — solid colour, a slightly
// darker shade on hover, white icon.
const fabSx = (bg, hover) => ({ bgcolor: bg, color: '#fff', '&:hover': { bgcolor: hover } })
